import memoize from 'moize'
import ms from 'ms'

import arriereBackoffice, {
  BookingItemSummaryKind,
  LeadSource,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { PreviewAlterDatesVariables } from '@new/domains/bookings/_types'
import { FullModifiedItemView } from '@new/domains/hotelTapeChart/components/HotelBookingSidePanel/components/AssignRoomForm'
import { MODIFIABLE_IN_VOYAGE_CUSTOM_FIELD_ID } from '@new/domains/hotelTapeChart/constants'
import { AlterHotelBookingArgs } from '@new/domains/hotelTapeChart/HotelTapeChart.types'

import { alterationPreviewSelection } from '@arriere/modules/bookings'

export const bookingsArriere = {
  fetchBookingById: memoize(
    async ({ bookingId }: { bookingId: string }) => {
      const booking = await arriereBackoffice.queries.booking
        .raw({
          __alias: 'getHotelBookingById',
          __args: {
            bookingId,
          },
          id: true,
          bookingGroupName: true,
          createdAt: true,
          cancellationInfo: {
            author: {
              name: true,
            },
          },
          checkInInstructionsLastSent: true,
          customFields: {
            section: {
              id: true,
            },
            fields: {
              id: true,
              value: true,
              name: true,
            },
          },
          detailedCheckIn: {
            effectiveTime: true,
          },
          detailedCheckOut: {
            effectiveTime: true,
          },
          emails: true,
          folio: {
            summary: {
              balance: true,
              isPaid: true,
              paid: {
                total: true,
              },
              paymentBalanceStatus: true,
              total: true,
              value: true,
            },
          },
          guestId: true,
          guestName: true,
          guests: true,
          groupBooking: { id: true, name: true },
          hash: true,
          home: {
            id: true,
            name: true,
            unitNumber: true,
            operationReadinessStatus: {
              status: true,
              cleanDetails: {
                status: true,
              },
              inspectedDetails: {
                status: true,
              },
            },
            region: {
              name: true,
              // The stay-action modals compare effectiveTime (property wall-clock) against now.
              timezone: true,
            },
          },
          homeId: true,
          hotelRoomType: {
            roomKind: true,
            title: true,
            hotelId: true,
            id: true,
          },
          isFromHotel: true,
          isRoomAssigned: true,
          issues: true,
          links: {
            manual: true,
            reservationHub: true,
            zendeskLink: true,
          },
          lockCode: {
            code: true,
          },
          lockStatus: {
            name: true,
            connectivity: true,
            offlineSince: true,
            updatedAt: true,
            batteryLevel: true,
            batteryStatus: true,
          },
          marketingInfo: {
            adults: true,
            children: true,
            infants: true,
            notes: true,
          },
          notes: true,
          paymentPlan: {
            value: true,
          },
          period: {
            start: true,
            end: true,
          },
          periodChanges: true,
          phones: true,
          reservationType: {
            name: true,
          },
          relocations: true,
          source: true,
          status: true,
          tags: {
            bookingTags: {
              short: true,
            },
            guestsTags: {
              short: true,
            },
          },
          hotel: {
            id: true,
            name: true,
          },
        })
        .then(async ({ data, errors }) => {
          if (errors?.length) {
            throw new Error(errors[errors?.length - 1]?.message)
          }
          return data
        })

      return booking
    },
    {
      maxAge: ms('5 minutes'),
    },
  ),
  fetchBookingAccessMethods: async ({ bookingId }: { bookingId: string }) => {
    return arriereBackoffice.queries.bookingAccessMethods({
      __alias: 'fetchBookingAccessMethods',
      __args: { bookingId },
      lockCodes: {
        code: true,
        expiration: true,
        id: true,
        lastChangeAt: true,
        validFrom: true,
        home: {
          id: true,
          name: true,
          unitNumber: true,
        },
      },
      keyCards: {
        homeId: true,
        error: true,
        items: {
          id: true,
          encoded: true,
        },
      },
    })
  },
}

export const previewHotelRoomAlteration = async ({
  bookingId,
  hotelRoomTypeId,
  newHomeId,
}: {
  bookingId: string
  hotelRoomTypeId: string
  newHomeId?: string
}) => {
  return arriereBackoffice.queries.booking.raw({
    __alias: 'previewHotelRoomAlteration',
    __args: {
      bookingId,
    },
    folio: {
      summary: {
        value: true,
        total: true,
      },
    },
    previewHotelAlteration: {
      __args: {
        newHomeId,
        hotelRoomTypeId,
        nightPrices: [],
      },
      newPrice: true,
      newSummary: {
        balance: true,
        total: true,
        value: true,
      },
    },
  })
}

const priceBreakdownFragment = {
  actual: {
    id: true,
    itemId: true,
    name: true,
    amount: true,
  },
  modified: {
    id: true,
    itemId: true,
    amountChange: {
      from: true,
      to: true,
    },
    name: true,
  },
  added: {
    id: true,
    itemId: true,
    name: true,
    amount: true,
  },
  nonModified: {
    id: true,
    itemId: true,
    name: true,
    amount: true,
  },
}

const priceBreakdownSummaryFragment = {
  valueNightsAndFees: true,
  balance: true,
  deposit: {
    balance: true,
  },
  isPaid: true,
  paid: {
    credits: true,
    total: true,
  },
  paymentBalanceStatus: true,
  total: true,
  value: true,
}

export const previewFullHotelAlteration = async ({
  bookingId,
  newHomeId,
  hotelRoomTypeId,
  newRange,
  nightPrices = [],
}: Partial<PreviewAlterDatesVariables> & { hotelRoomTypeId: string }) => {
  return arriereBackoffice.queries.booking
    .raw({
      __alias: 'previewFullHotelAlteration',
      __args: {
        bookingId,
      },
      folio: {
        items: {
          id: true,
          kind: true,
          title: true,
          date: true,
          total: true,
        },
        summary: {
          ...priceBreakdownSummaryFragment,
        },
      },
      previewHotelAlteration: {
        __args: {
          nightPrices,
          hotelRoomTypeId,
          newHomeId,
          newRange,
          includeAvailabilityInfo: true,
        },
        nights: {
          date: true,
          price: true,
        },
        feesBreakdown: {
          ...priceBreakdownFragment,
        },
        taxesBreakdown: {
          ...priceBreakdownFragment,
        },
        newPrice: true,
        newSummary: {
          ...priceBreakdownSummaryFragment,
        },
        availabilityDetails: {
          isAvailable: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      const nightsItems = data.folio?.items?.filter(
        item => item.kind === BookingItemSummaryKind.night,
      )

      const modifiedNights = nightsItems?.reduce((result: FullModifiedItemView[], night) => {
        const nightPrice = data.previewHotelAlteration?.nights.find(
          nightItem => nightItem.date === night.date,
        )?.price

        if (nightPrice !== undefined && night.total !== nightPrice) {
          return [
            ...result,
            {
              id: night.date as string,
              date: night.date,
              itemId: night.id,
              name: night.title,
              amountChange: {
                from: night.total,
                to: nightPrice,
              },
            },
          ]
        }

        return result
      }, [])

      const actualNights = nightsItems?.map(night => ({
        id: night.date,
        date: night.date,
        itemId: night.id,
        name: night.title,
        amount: night.total,
      }))

      const modifiedNightIds = modifiedNights.map(night => night.itemId)

      const nightsBreakdown = {
        actual: actualNights,
        modified: modifiedNights,
        nonModified: actualNights.filter(
          nightItem => !modifiedNightIds?.includes(nightItem.itemId),
        ),
        added: undefined,
      }

      return {
        summary: { ...data.folio.summary },
        nightsBreakdown,
        ...data.previewHotelAlteration,
      }
    })
}

export const previewHotelAlterationForModify = async ({
  bookingId,
  hotelRoomTypeId,
  newHomeId,
  newRange,
  nightPrices = [],
  feeOverrides,
  taxExemption,
  lockedPrice,
  queryAlias,
}: Partial<PreviewAlterDatesVariables> & { hotelRoomTypeId: string; lockedPrice?: boolean }) => {
  return arriereBackoffice.queries.booking
    .raw({
      __alias: queryAlias || 'previewHotelAlterationForModify',
      __args: { bookingId },
      previewHotelAlteration: {
        __args: {
          nightPrices,
          hotelRoomTypeId,
          newHomeId,
          newRange,
          feeOverrides,
          taxExemption,
          lockedPrice,
          includeAvailabilityInfo: true,
        },
        ...alterationPreviewSelection,
        availabilityDetails: { isAvailable: true },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors.length - 1]?.message)
      }

      return data.previewHotelAlteration
    })
}

// Mutations

/**
 * System rule (BODI-2970): an Airbnb room move applies in Voyage only, so the booking has to be
 * flagged modifiable before it. Enforced on every room-move mutation below rather than in each
 * caller's store, so a new flow inherits it instead of having to remember it.
 */
const unlockAirbnbBookingForRoomMove = async (
  bookingId: string,
  bookingSource: LeadSource | undefined,
) => {
  if (bookingSource !== LeadSource.airBnb) return

  const flagged = await setBookingModifiableInVoyage(bookingId)

  if (!flagged) throw new Error('Failed to flag booking as modifiable in Voyage')
}

export const assignRoomToBooking = async (
  bookingId: string,
  homeId: string,
  bookingSource: LeadSource | undefined,
) => {
  await unlockAirbnbBookingForRoomMove(bookingId, bookingSource)

  return arriereBackoffice.mutations.booking
    .raw({
      __alias: 'assignRoomToBooking',
      assignRoomToBooking: {
        __args: {
          bookingId,
          homeId,
        },
        success: true,
        failureDetails: {
          swaps: {
            conflictWith: {
              label: true,
            },
          },
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data.assignRoomToBooking
    })
}

export const alterHotelBooking = async ({ bookingSource, ...args }: AlterHotelBookingArgs) => {
  await unlockAirbnbBookingForRoomMove(args.bookingId, bookingSource)

  return arriereBackoffice.mutations.booking
    .raw({
      __alias: 'alterHotelBooking',
      alterHotelBooking: {
        __args: {
          ...args,
          roomAllocationEnabled: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      // To match assignRoomToBooking result type
      return { success: data.alterHotelBooking, failureDetails: undefined }
    })
}

export const setBookingModifiableInVoyage = async (bookingId: string) => {
  return arriereBackoffice.mutations.updateBookingCustomFieldValues({
    __args: {
      bookingId,
      fieldValues: [
        {
          customFieldId: MODIFIABLE_IN_VOYAGE_CUSTOM_FIELD_ID,
          value: 'true',
        },
      ],
    },
  })
}

export const unassignRoomFromBooking = async (bookingId: string) => {
  return arriereBackoffice.mutations.booking
    .raw({
      __alias: 'unassignRoomFromBooking',
      unassignRoomBookings: {
        __args: {
          bookingIds: [bookingId],
        },
        completed: true,
        invalid: {
          bookingId: true,
          reason: true,
        },
      },
    })
    .then(({ data, errors }) => {
      if (errors?.length) {
        throw new Error(errors[errors?.length - 1]?.message)
      }

      return data.unassignRoomBookings
    })
}
